// Onglet d'atterrissage quand on CLIQUE une ligne dans la liste des commandes/operations.
//
// POURQUOI une fonction dediee, et non un `?tab=<index>` code en dur (l'ancien comportement) :
//   - l'index d'un onglet depend des onglets presents, qui varient (le BC acheteur a des onglets par
//     rang) : `?tab=2` ne designe pas le meme onglet partout ;
//   - le statut se lit par ID (jamais par `nom`, qui change en base) ;
//   - la destination est un KIND d'onglet, que les hooks de chaque case resolvent deja
//     (useOperationTabs / useCommandeTabs acceptent `?tab=<kind>`).
//
// La destination depend de la SOURCE (BC/MS ont des enums de statut distincts, memes ids sens
// different) et du ROLE (acheteur vs prestataire n'ont pas les memes onglets : le MS acheteur analyse,
// le MS presta candidate). D'ou deux fonctions, une par role.
//
// Regles metier validees :
//   BC : brouillon -> Consultation ; a traiter -> Offre ; valide/accepte -> Notification ;
//        refuse/expire/annule -> Historique.
//   MS : a candidater -> Consultation ; a analyser -> Analyse ; signe/valide -> Notifications ;
//        refuse/expire/annule -> Historique.
//   Presta : meme logique, transposee sur ses propres onglets.

// Ids BC (bdc.status, vue BC). Repris des enums StatusId des cases BC.
const BC = {
  BrouillonAcheteur: 1,
  BrouillonPrestataire: 2,
  AttenteAcceptationRang1: 3,
  AAccepter: 4,
  AttenteAcceptationRang2: 5,
  AttenteAcceptationRang3: 6,
  Valide: 7,
  Refuse: 8,
  Expire: 9,
  AttenteOffre: 10,
  AChiffrer: 11,
  AAnalyser: 12,
  AttenteAnalyse: 13,
  Annule: 14,
  PropositionFaite: 15,
} as const;

// Ids MS (subsequent.status, vue MS).
const MS = {
  Brouillon: 1,
  ConsultationEnCours: 2,
  ACandidater: 3,
  AAnalyser: 4,
  EnAttenteAnalyse: 5,
  Refuser: 6,
  ASigner: 7,
  EnAttenteSignaturePrestataire: 8,
  EnAttenteSignatureAcheteur: 9,
  ASignerPrestataire: 10,
  Valider: 11,
  Expirer: 12,
  ANotifier: 13,
  Annule: 14,
} as const;

const estBC = (source?: string): boolean => (source ?? '').toUpperCase().trim() === 'BC';
const estMS = (source?: string): boolean => (source ?? '').toUpperCase().trim() === 'MS';

/**
 * Onglet cible cote ACHETEUR (pages/Operation -> operation-bc / operation-ms).
 * `null` = pas de regle specifique (l'appelant garde son fallback : BCI, statuts inconnus).
 */
export const acheteurRowTabKind = (source: string | undefined, statusId: number): string | null => {
  if (estBC(source)) {
    switch (statusId) {
      case BC.Valide:
        return 'notification'; // accepte/valide : le bon de commande signe se consulte ici
      case BC.Refuse:
      case BC.Expire:
      case BC.Annule:
        return 'historique';
      case BC.BrouillonAcheteur:
      case BC.BrouillonPrestataire:
        return 'consultation';
      default:
        // toute decision en cours (a accepter, a chiffrer, offre recue, a analyser...) : l'onglet Offre
        return 'offre';
    }
  }
  if (estMS(source)) {
    switch (statusId) {
      case MS.Valider:
      case MS.ASigner:
      case MS.EnAttenteSignaturePrestataire:
      case MS.EnAttenteSignatureAcheteur:
      case MS.ASignerPrestataire:
      case MS.ANotifier:
        return 'notifications';
      case MS.Refuser:
      case MS.Expirer:
      case MS.Annule:
        return 'historique';
      case MS.AAnalyser:
      case MS.EnAttenteAnalyse:
        return 'analyse';
      case MS.Brouillon:
      case MS.ConsultationEnCours:
      case MS.ACandidater:
        return 'consultation';
      default:
        return null;
    }
  }
  return null; // BCI et autres : comportement inchange, l'appelant decide
};

/**
 * Onglet cible cote PRESTATAIRE (pages/Commandes -> commande-bc / commande-ms).
 * Meme logique, transposee : le presta CHIFFRE/repond dans l'onglet Offre (BC) ou Candidature (MS),
 * la ou l'acheteur analyse. Le kind "Offre" du presta MS est `candidature` (dissonance kind/label).
 */
export const prestaRowTabKind = (source: string | undefined, statusId: number): string | null => {
  if (estBC(source)) {
    switch (statusId) {
      case BC.Valide:
        return 'notification';
      case BC.Refuse:
      case BC.Expire:
      case BC.Annule:
        return 'historique';
      case BC.BrouillonAcheteur:
        return 'consultation';
      default:
        // BrouillonPrestataire (a chiffrer), AAccepter, AChiffrer, AttenteAnalyse (offre envoyee)... :
        // tout ce que le presta traite/consulte se joue dans son onglet Offre.
        return 'offre';
    }
  }
  if (estMS(source)) {
    switch (statusId) {
      case MS.Valider:
      case MS.ASigner:
      case MS.EnAttenteSignaturePrestataire:
      case MS.EnAttenteSignatureAcheteur:
      case MS.ASignerPrestataire:
      case MS.ANotifier:
        return 'notifications';
      case MS.Refuser:
      case MS.Expirer:
      case MS.Annule:
        return 'historique';
      case MS.ACandidater:
      case MS.EnAttenteAnalyse:
        // le presta candidate / consulte sa candidature : kind `candidature` (label "Offre")
        return 'candidature';
      case MS.Brouillon:
      case MS.ConsultationEnCours:
      case MS.AAnalyser:
        return 'consultation';
      default:
        return null;
    }
  }
  return null;
};
